import React from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  Instagram,
  Facebook,
  ExternalLink,
} from "lucide-react";
import { IMAGES } from "@/assets/images";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

const contactSchema = z.object({
  nombre: z.string().min(2, "El nombre es demasiado corto"),
  email: z.string().email("Email inválido"),
  asunto: z.string().min(5, "Por favor especifique un asunto"),
  mensaje: z.string().min(10, "El mensaje debe ser más descriptivo"),
});

type ContactFormValues = z.infer<typeof contactSchema>;

export default function Contacto() {
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nombre: "",
      email: "",
      asunto: "",
      mensaje: "",
    },
  });

  const onSubmit = (data: ContactFormValues) => {
    console.log("Form submitted:", data);
    toast.success("Mensaje enviado correctamente. Nos pondremos en contacto pronto.");
    form.reset();
  };

  return (
    <Layout>
      <div className="flex flex-col min-h-screen">
        {/* Hero Section */}
        <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
          <motion.div
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute inset-0 z-0"
          >
            <img
              src={IMAGES.HERO_BEACH_3}
              alt="Playa El Maviri"
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/40 to-background" />
          </motion.div>

          <div className="relative z-10 text-center px-4">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
              className="text-5xl md:text-7xl font-semibold tracking-tighter text-primary mb-4"
            >
              Contacto
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, ...springPresets.gentle }}
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-light"
            >
              Estamos ubicados en el corazón de Playa El Maviri, listos para brindarte la mejor experiencia culinaria y de descanso.
            </motion.p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-24 px-4 container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Contact Info */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-12"
            >
              <motion.div variants={staggerItem}>
                <h2 className="text-3xl font-semibold mb-6 text-foreground">Información de Contacto</h2>
                <p className="text-muted-foreground mb-8 text-lg">
                  ¿Tienes alguna duda sobre nuestras cabañas o quieres reservar para un evento especial en Las Gaviotas o Medusas? Escríbenos.
                </p>
              </motion.div>

              <div className="space-y-6">
                <motion.div variants={staggerItem} className="flex items-start gap-5">
                  <div className="p-4 rounded-2xl bg-secondary flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Ubicación</h3>
                    <p className="text-muted-foreground">Isla El Maviri, Carretera Topolobampo-Maviri, Ahome, Sinaloa, México.</p>
                  </div>
                </motion.div>

                <motion.div variants={staggerItem} className="flex items-start gap-5">
                  <div className="p-4 rounded-2xl bg-secondary flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Teléfonos</h3>
                    <p className="text-muted-foreground">+52 (668) 123 4567 (Restaurante)</p>
                    <p className="text-muted-foreground">+52 (668) 987 6543 (Reservaciones Cabañas)</p>
                  </div>
                </motion.div>

                <motion.div variants={staggerItem} className="flex items-start gap-5">
                  <div className="p-4 rounded-2xl bg-secondary flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Correo Electrónico</h3>
                    <p className="text-muted-foreground">hola@lasgaviotasmaviri.com</p>
                    <p className="text-muted-foreground">reservas@lasgaviotasmaviri.com</p>
                  </div>
                </motion.div>

                <motion.div variants={staggerItem} className="flex items-start gap-5">
                  <div className="p-4 rounded-2xl bg-secondary flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Horarios de Atención</h3>
                    <p className="text-muted-foreground">Restaurantes: Lun - Dom | 10:00 AM - 8:00 PM</p>
                    <p className="text-muted-foreground">Cabañas: Recepción 24 Horas</p>
                  </div>
                </motion.div>
              </div>

              <motion.div variants={staggerItem} className="pt-8 flex gap-4">
                <Button variant="outline" size="icon" className="rounded-full w-12 h-12">
                  <Instagram className="w-5 h-5" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full w-12 h-12">
                  <Facebook className="w-5 h-5" />
                </Button>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="bg-card border border-border p-8 md:p-12 rounded-[2rem] shadow-sm"
            >
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="nombre"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nombre</FormLabel>
                          <FormControl>
                            <Input placeholder="Tu nombre" className="bg-background/50" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input placeholder="tu@email.com" className="bg-background/50" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="asunto"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Asunto</FormLabel>
                        <FormControl>
                          <Input placeholder="¿En qué podemos ayudarte?" className="bg-background/50" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="mensaje"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mensaje</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Escribe aquí los detalles de tu consulta..."
                            className="min-h-[150px] bg-background/50"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full py-6 rounded-xl group">
                    Enviar Mensaje
                    <Send className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </form>
              </Form>
            </motion.div>
          </div>
        </section>

        {/* Map Section */}
        <section className="container mx-auto px-4 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.smooth}
            className="relative h-[500px] w-full rounded-[2.5rem] overflow-hidden border border-border"
          >
            {/* Placeholder for Map - In a real app we'd use Google Maps or Leaflet */}
            <div className="absolute inset-0 bg-muted flex flex-col items-center justify-center text-center p-8">
              <div className="p-6 rounded-full bg-background mb-4 shadow-sm">
                <MapPin className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold mb-2">¿Cómo llegar?</h3>
              <p className="text-muted-foreground max-w-md">
                Toma la carretera Mochis-Topolobampo y desvíate hacia el puente El Maviri. Sigue recto hasta llegar al complejo Las Gaviotas.
              </p>
              <Button variant="secondary" className="mt-6 group">
                Abrir en Google Maps
                <ExternalLink className="ml-2 w-4 h-4 transition-transform group-hover:scale-110" />
              </Button>
            </div>
            {/* Stylized visual elements for the "map" background */}
            <div className="absolute inset-0 pointer-events-none opacity-20">
              <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full border-4 border-primary/20" />
              <div className="absolute bottom-1/3 right-1/4 w-64 h-2 bg-primary/10 rotate-12" />
              <div className="absolute top-1/2 left-1/2 w-96 h-96 border border-primary/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
            </div>
          </motion.div>
        </section>
      </div>
    </Layout>
  );
}
